import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Bars, Chip, Kpis, Modal, Panel, Segments, useFlash } from '../../ui/kit.js';
import { Done, Note } from '../../ui/actions/parts.js';
import { day } from '../../state/building.js';

const PERIODS = ['30 days', '90 days', '12 months'];

const DATA = {
    '30 days': {
        kpis: { closed: 86, resolution: '1.8 days', bookings: 342, adoption: 96, packages: 1284 },
        amenities: [
            { label: 'Pool cabanas', value: 118 }, { label: 'Fitness studio', value: 94 }, { label: 'Spa treatment rooms', value: 51 },
            { label: 'Rooftop lounge', value: 38 }, { label: 'Private dining', value: 24 }, { label: 'Screening room', value: 17 }
        ],
        categories: [
            { label: 'HVAC', value: 27 }, { label: 'Plumbing', value: 22 }, { label: 'Appliance', value: 14 },
            { label: 'Electrical', value: 11 }, { label: 'Doors & locks', value: 8 }, { label: 'Access', value: 4 }
        ],
        valet: [
            { label: '6 to 9 AM', value: 212 }, { label: '9 AM to noon', value: 148 }, { label: 'Noon to 3 PM', value: 121 },
            { label: '3 to 6 PM', value: 196 }, { label: '6 to 9 PM', value: 284 }, { label: '9 PM to 6 AM', value: 97 }
        ],
        satisfaction: { score: 4.7, responses: 138, trend: '+0.1 vs prior period', quote: 'Valet is faster than ever and the app tells me exactly when my car is up.' }
    },
    '90 days': {
        kpis: { closed: 247, resolution: '2.1 days', bookings: 1016, adoption: 94, packages: 3911 },
        amenities: [
            { label: 'Pool cabanas', value: 362 }, { label: 'Fitness studio', value: 271 }, { label: 'Spa treatment rooms', value: 149 },
            { label: 'Rooftop lounge', value: 112 }, { label: 'Private dining', value: 70 }, { label: 'Screening room', value: 52 }
        ],
        categories: [
            { label: 'HVAC', value: 84 }, { label: 'Plumbing', value: 61 }, { label: 'Appliance', value: 39 },
            { label: 'Electrical', value: 30 }, { label: 'Doors & locks', value: 21 }, { label: 'Access', value: 12 }
        ],
        valet: [
            { label: '6 to 9 AM', value: 640 }, { label: '9 AM to noon', value: 452 }, { label: 'Noon to 3 PM', value: 371 },
            { label: '3 to 6 PM', value: 588 }, { label: '6 to 9 PM', value: 861 }, { label: '9 PM to 6 AM', value: 290 }
        ],
        satisfaction: { score: 4.6, responses: 402, trend: '+0.3 vs prior period', quote: 'Maintenance fixed my AC the same afternoon I reported it.' }
    },
    '12 months': {
        kpis: { closed: 1038, resolution: '2.6 days', bookings: 3874, adoption: 96, packages: 15620 },
        amenities: [
            { label: 'Pool cabanas', value: 1290 }, { label: 'Fitness studio', value: 1104 }, { label: 'Spa treatment rooms', value: 588 },
            { label: 'Rooftop lounge', value: 431 }, { label: 'Private dining', value: 276 }, { label: 'Screening room', value: 185 }
        ],
        categories: [
            { label: 'HVAC', value: 362 }, { label: 'Plumbing', value: 248 }, { label: 'Appliance', value: 161 },
            { label: 'Electrical', value: 127 }, { label: 'Doors & locks', value: 92 }, { label: 'Access', value: 48 }
        ],
        valet: [
            { label: '6 to 9 AM', value: 2560 }, { label: '9 AM to noon', value: 1795 }, { label: 'Noon to 3 PM', value: 1488 },
            { label: '3 to 6 PM', value: 2344 }, { label: '6 to 9 PM', value: 3402 }, { label: '9 PM to 6 AM', value: 1170 }
        ],
        satisfaction: { score: 4.5, responses: 1611, trend: '+0.6 since launch', quote: 'Booking the cabanas and the private dining room takes seconds now.' }
    }
};

/* Analytics / Executive Reporting: the figures a board meeting asks for,
   by period, and a report management can take into the room. */
function AnalyticsScreen() {
    const [period, setPeriod] = useState('30 days');
    const [flash, notify] = useFlash();
    const [report, setReport] = useState(false);
    const data = DATA[period];

    return (
        <div className="screen-content">
            <ScreenHead
                eyebrow="Executive reporting"
                title="Analytics"
                aside={<Segments label="Reporting period" options={PERIODS} value={period} onChange={setPeriod} />}
            />

            <Kpis items={[
                { label: 'Work orders closed', value: data.kpis.closed.toLocaleString(), tone: 'ok' },
                { label: 'Avg resolution', value: data.kpis.resolution, tone: 'info' },
                { label: 'Amenity bookings', value: data.kpis.bookings.toLocaleString() },
                { label: 'App adoption', value: `${data.kpis.adoption}%`, note: 'of residents active', tone: 'ok' },
                { label: 'Packages handled', value: data.kpis.packages.toLocaleString() }
            ]} />

            {flash}

            <div className="cards cards--wide">
                <Panel index={0}>
                    <h4>Amenity usage</h4>
                    <Bars items={data.amenities} />
                </Panel>
                <Panel index={1}>
                    <h4>Work orders by category</h4>
                    <Bars items={data.categories} />
                </Panel>
                <Panel index={2}>
                    <h4>Valet requests by time of day</h4>
                    <Bars items={data.valet} />
                </Panel>
                <Panel index={3} className="panel--accent">
                    <div className="row__end" style={{ justifyContent: 'space-between' }}>
                        <h4>Resident satisfaction</h4>
                        <Chip tone="ok">{data.satisfaction.trend}</Chip>
                    </div>
                    <span className="kpi__value">{data.satisfaction.score} / 5</span>
                    <p className="row__meta">{data.satisfaction.responses.toLocaleString()} survey responses · last {period}</p>
                    <p>"{data.satisfaction.quote}"</p>
                    {/* Used to announce a PDF nobody saw. It now shows the
                        report first, and sends it from there. */}
                    <button type="button" className="btn btn-primary" onClick={() => setReport(true)}>
                        Export board report
                    </button>
                </Panel>
            </div>

            <ReportSheet open={report} period={period} data={data} onClose={() => setReport(false)} onSent={notify} />
        </div>
    );
}

const top = list => list.reduce((a, b) => (b.value > a.value ? b : a));

/* The board report as it will print: the period's headline figures on
   one page, then sent to the board or saved. */
function ReportSheet({ open, period, data, onClose, onSent }) {
    const [sent, setSent] = useState(false);
    const [note, setNote] = useState(null);
    const [wasOpen, setWasOpen] = useState(open);

    if (open !== wasOpen) {
        setWasOpen(open);
        if (open) { setSent(false); setNote(null); }
    }

    const file = `Board report, last ${period}.pdf`;

    const email = () => {
        setSent(true);
        onSent(`Board report for the last ${period} emailed to the five directors and the management office.`);
    };

    const save = () => setNote({ text: `Saved to management files as "${file}".`, key: Date.now() });

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="lg"
            eyebrow="Executive reporting"
            title={sent ? 'Report sent' : 'Board report preview'}
            actions={sent
                ? <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
                : (
                    <>
                        <button type="button" className="btn btn-outline-light" onClick={save}>Save PDF</button>
                        <button type="button" className="btn btn-primary" onClick={email}>Email to the board</button>
                    </>
                )}
        >
            {sent ? (
                <Done title="On its way to the board">
                    <p>"{file}" went to the five directors and the management office, {day(0)}. Replies come back to the management inbox.</p>
                </Done>
            ) : (
                <>
                    <article className="doc-page report-page">
                        <span className="doc-page__org">Condominium Association · Board of Directors</span>
                        <h4 className="doc-page__title">Operations report, last {period}</h4>
                        <span className="doc-page__meta">Prepared {day(0)} by building management</span>
                        <dl className="report-page__figures">
                            <div><dt>Work orders closed</dt><dd>{data.kpis.closed.toLocaleString()}</dd></div>
                            <div><dt>Average resolution</dt><dd>{data.kpis.resolution}</dd></div>
                            <div><dt>Amenity bookings</dt><dd>{data.kpis.bookings.toLocaleString()}</dd></div>
                            <div><dt>Packages handled</dt><dd>{data.kpis.packages.toLocaleString()}</dd></div>
                            <div><dt>App adoption</dt><dd>{data.kpis.adoption}%</dd></div>
                            <div><dt>Satisfaction</dt><dd>{data.satisfaction.score} / 5</dd></div>
                        </dl>
                        <ul className="report-page__notes">
                            <li>Busiest amenity: {top(data.amenities).label.toLowerCase()}, {top(data.amenities).value.toLocaleString()} bookings.</li>
                            <li>Most common repair: {top(data.categories).label}, {top(data.categories).value.toLocaleString()} work orders.</li>
                            <li>Valet peak: {top(data.valet).label}, {top(data.valet).value.toLocaleString()} requests.</li>
                            <li>Resident satisfaction {data.satisfaction.trend}, from {data.satisfaction.responses.toLocaleString()} responses.</li>
                        </ul>
                        <p className="doc-page__text">"{data.satisfaction.quote}"</p>
                        <span className="doc-page__foot">Page 1 of 4</span>
                    </article>
                    <Note note={note} />
                </>
            )}
        </Modal>
    );
}

export default AnalyticsScreen;
